import { HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core'
import { Keepalive } from '@ng-idle/keepalive'
import { EMPTY, Observable, catchError, finalize, map, tap } from 'rxjs'
import { PulsarWebSocketService } from 'src/app/data/services/application/pulsar-websocket/pulsar-websocket.service'
import { ThemeService } from 'src/app/data/services/application/theme/theme.service'
import { UserService } from 'src/app/data/services/application/user/user.service'
import { ToastService } from 'src/app/data/services/components/toast/toast.service'
import { StorageKeys } from 'src/app/domain/enums/application/storage-keys.enum'
import { Theme } from 'src/app/domain/enums/application/theme.enum'
import { ToastSeverity } from 'src/app/domain/enums/components/toast.enum'
import { ApiResponse } from 'src/app/domain/interfaces/application/api-response.interface'
import { IAuthResponse } from 'src/app/domain/interfaces/application/auth-response.interface'
import { Toast } from 'src/app/domain/interfaces/components/toast.interface'
import { Md5Hash } from '../../domain/utils/classes/md5-hash'
import { AuthRepositoryService } from './auth-repository.service'
import { TokenService } from './token/token.service'

@Injectable({ providedIn: 'root' })
export class AuthService {
  private authRepositoryService: AuthRepositoryService = inject(AuthRepositoryService)
  private themeService: ThemeService = inject(ThemeService)
  private toastService: ToastService = inject(ToastService)
  private idle: Idle = inject(Idle)
  private keepalive: Keepalive = inject(Keepalive)
  private tokenService: TokenService = inject(TokenService)
  private userService: UserService = inject(UserService)
  private pulsarWebSocketService: PulsarWebSocketService = inject(PulsarWebSocketService)

  hashPassword: Md5Hash = new Md5Hash()
  idleState = 'NOT_STARTED'
  isRefreshingToken = false

  timeToInactiveUserInSeconds = 60 * 60 * 6
  timetoGetNewTokenInMinutes = 5
  timeToClearSessionUser = 60
  timeToCheckTokenUserInSeconds = 60 * 5

  public auth2fa(user: string, password: string): Observable<IAuthResponse> {
    const payload: any = { email: user, password: this.hashPassword.b64_md5(password) }

    return this.authRepositoryService.auth2fa(payload).pipe(
      map((res: HttpResponse<ApiResponse<IAuthResponse>>) => {
        const data: IAuthResponse | undefined = res?.body?.data

        this.tokenService.setToken(data?.accessToken ?? '')
        localStorage.setItem(StorageKeys.KEY_2FA_PREFERRED_AUTH, String(data?.preferredAuth ?? ''))
        localStorage.setItem(StorageKeys.KEY_2FA_CODE_SENT, String(data?.codeSent ?? ''))
        localStorage.setItem(StorageKeys.KEY_2FA_REQUEST_USER_PHONE, String(data?.requestUserPhone ?? ''))
        localStorage.setItem(StorageKeys.KEY_2FA_SECRET_URI, String(data?.secretUri ?? ''))
        localStorage.setItem(StorageKeys.KEY_2FA_TOTP_VALIDATED, String(data?.totpValidated ?? ''))
        this.themeService.setTheme(Theme.DEFAULT)

        return data
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public verify2faCode(code: string, preferredAuth: string): Observable<HttpResponse<ApiResponse>> {
    const payload: any = { code: code, preferredAuth: preferredAuth }

    return this.authRepositoryService.verify2faCode(payload).pipe(
      map((res: any) => {
        const data: any = res.body.data

        this.tokenService.setToken(data.accessToken)
        this.tokenService.setRefreshToken(data.refreshToken)
        this.tokenService.setExpirationToken(data.expirationToken.toString())
        this.tokenService.setRefreshTokenExpirationTime(data.refreshTokenExpirationTime.toString())
        this.userService.setAuthUser({ ...data.userInformation, email: data.userEmail })
        this.themeService.setTheme(Theme.DEFAULT)
        this.pulsarWebSocketService.syncSessionFromRefreshToken()
        return data
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public sendWhatsappCode(whatsappNumber: number | null): Observable<HttpResponse<ApiResponse>> {
    const payload: any = { whatsappNumber: whatsappNumber }

    return this.authRepositoryService.sendWhatsappCode(payload).pipe(
      map((res: any) => {
        return res.body.data
      })
    )
  }

  public sendWhatsappCodeByEmail(): Observable<HttpResponse<ApiResponse>> {
    return this.authRepositoryService.sendWhatsappCodeByEmail().pipe(
      map((res: any) => {
        return res.body.data
      })
    )
  }

  public refreshToken(): void {
    if (this.isRefreshingToken) return

    this.isRefreshingToken = true
    const refreshToken = this.tokenService.getRefreshToken()

    this.authRepositoryService
      .refreshToken(refreshToken)
      .pipe(finalize(() => (this.isRefreshingToken = false)))
      .subscribe((res) => {
        const data = res.body.data

        this.tokenService.setToken(data.accessToken)
        this.tokenService.setRefreshToken(data.refreshToken)
        this.tokenService.setExpirationToken(data.expirationToken.toString())
        this.tokenService.setRefreshTokenExpirationTime(data.refreshTokenExpirationTime.toString())
        this.pulsarWebSocketService.syncSessionFromRefreshToken()
      })
  }

  public startIddle(): void {
    this.idle.setIdle(this.timeToInactiveUserInSeconds)
    this.idle.setTimeout(this.timeToClearSessionUser)
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES)

    this.idle.onIdleStart.subscribe(() => {
      this.idleState = 'IDLE'
    })

    this.idle.onIdleEnd.subscribe(() => {
      this.idleState = 'NOT_IDLE'

      if (this.tokenService.hasToken() && this.tokenService.isRefreshTokenExpired()) {
        this.userService.logout()
        return
      }

      this.resetIddleVerification()
    })

    this.idle.onTimeout.subscribe(() => {
      this.userService.logout()
      this.idleState = 'TIMED_OUT'
      this.resetIddleVerification()
    })

    this.keepalive.interval(this.timeToCheckTokenUserInSeconds)

    this.keepalive.onPing.subscribe(() => {
      if (this.tokenService.hasToken() && this.tokenService.isAccessTokenExpiring(this.timetoGetNewTokenInMinutes)) this.refreshToken()
    })

    document.addEventListener('visibilitychange', () => this.onVisibilityChange())

    this.resetIddleVerification()
  }

  private onVisibilityChange(): void {
    if (document.visibilityState !== 'visible' || !this.tokenService.hasToken()) return

    if (this.tokenService.isRefreshTokenExpired()) {
      this.userService.logout()
      return
    }

    if (this.tokenService.isAccessTokenExpiring(this.timetoGetNewTokenInMinutes)) {
      this.refreshToken()
    }
  }

  private resetIddleVerification() {
    this.idle.watch()
    this.idleState = 'NOT_IDLE'
  }

  public sendCodeEmailResetPassword(userMail: string): Observable<HttpResponse<ApiResponse>> {
    const payload = { email: userMail }

    return this.authRepositoryService.sendCodeEmailResetPassword(payload).pipe(
      tap((res: HttpResponse<ApiResponse>) => {
        return res
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public verifyPasswordResetCodeEmail(userMail: string, code: string): Observable<HttpResponse<ApiResponse>> {
    const payload = { email: userMail, code: code }

    return this.authRepositoryService.verifyPasswordResetCodeEmail(payload).pipe(
      tap((res: HttpResponse<ApiResponse>) => {
        return res
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public resetPassword(userToken: string, userMail: string, newPassword: string): Observable<HttpResponse<any>> {
    const payload = { token: userToken, email: userMail, newPassword: this.hashPassword.b64_md5(newPassword) }

    return this.authRepositoryService.resetPassword(payload).pipe(
      tap((res: HttpResponse<any>) => {
        return res
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao atualizar senha!' } as Toast)
        return EMPTY
      })
    )
  }

  public resetPasswordFirstAccess(newPassword: string): Observable<HttpResponse<any>> {
    const payload = { newPassword: this.hashPassword.b64_md5(newPassword) }

    return this.authRepositoryService.resetPasswordFirstAccess(payload).pipe(
      tap((res: HttpResponse<any>) => {
        return res
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao atualizar senha!' } as Toast)
        return EMPTY
      })
    )
  }
}
